/**
 * DRPX Courier — Design-Tokens & Theme
 *
 * `getThemeVars()` liefert CSS-Variablen für Dark/Light-Mode.
 * `S` ist das zentrale Style-Token-Objekt für alle Inline-Styles.
 *
 * @module config/theme
 */

import type { CSSProperties } from 'react';

/** CSS-Custom-Properties für ein Theme-Preset. */
type ThemeVars = Record<string, string>;

/** Gibt CSS-Variablen für Dark- oder Light-Mode zurück. */
export function getThemeVars(dark: boolean, neutralBg: boolean = false): ThemeVars {
  return dark
    ? {
        '--bg': '#000000',
        '--surface': '#1c1c1e',
        '--surface2': '#2c2c2e',
        '--surface3': '#3a3a3c',
        '--border': 'rgba(255,255,255,.08)',
        '--border2': 'rgba(255,255,255,.15)',
        '--accent': '#4ade80',
        '--accent2': '#22c55e',
        '--accent-light': '#86efac',
        '--text': 'rgba(255,255,255,.95)',
        '--muted': 'rgba(255,255,255,.6)',
        '--muted2': 'rgba(255,255,255,.4)',
        '--success': '#4ade80',
        '--danger': '#fb7185',
        '--amber': '#f59e0b',
        '--amber-light': '#fbbf24',
        '--amber-glow': 'rgba(245,158,11,.08)',
        '--glass': '#1c1c1e',
        '--glow': 'rgba(0,0,0,.3)',
      }
    : neutralBg
      ? {
          '--bg': '#f8fafc',
          '--surface': 'rgba(255,255,255,.95)',
          '--surface2': 'rgba(241,245,249,.8)',
          '--surface3': 'rgba(226,232,240,.6)',
          '--border': 'rgba(0,0,0,.06)',
          '--border2': 'rgba(0,0,0,.1)',
          '--accent': '#16a34a',
          '--accent2': '#15803d',
          '--accent-light': '#22c55e',
          '--text': '#0c1a0e',
          '--muted': '#64748b',
          '--muted2': '#94a3b8',
          '--success': '#16a34a',
          '--danger': '#ef4444',
          '--amber': '#d97706',
          '--amber-light': '#f59e0b',
          '--amber-glow': 'rgba(217,119,6,.06)',
          '--glass': 'rgba(255,255,255,.85)',
          '--glow': 'rgba(0,0,0,.02)',
        }
      : {
          '--bg': '#f5f5f7',
          '--surface': 'rgba(255,255,255,1)',
          '--surface2': 'rgba(255,255,255,.85)',
          '--surface3': 'rgba(245,245,247,.85)',
          '--border': '#e5e5e7',
          '--border2': '#d1d1d6',
          '--accent': '#16a34a',
          '--accent2': '#15803d',
          '--accent-light': '#22c55e',
          '--text': '#1d1d1f',
          '--muted': '#6e6e73',
          '--muted2': '#86868b',
          '--success': '#16a34a',
          '--danger': '#ef4444',
          '--amber': '#d97706',
          '--amber-light': '#f59e0b',
          '--amber-glow': 'rgba(217,119,6,.06)',
          '--glass': 'rgba(255,255,255,.95)',
          '--glow': 'rgba(0,0,0,.04)',
        };
}

/** S — Zentrale Style-Konstanten (Buttons, Inputs, Layout, Typografie). Inline-Styles statt CSS-Klassen. */
export const S = {
  // ═══════════════════════════════════════════════════════════
  // LAYOUT — Flex-Utilities (ersetzen 170+ Inline-Stellen)
  // ═══════════════════════════════════════════════════════════
  row: { display: 'flex', alignItems: 'center' },
  rowBetween: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  rowEnd: { display: 'flex', justifyContent: 'flex-end' },
  rowWrap: { display: 'flex', flexWrap: 'wrap', alignItems: 'center' },
  col: { display: 'flex', flexDirection: 'column' },

  // ═══════════════════════════════════════════════════════════
  // TYPOGRAFIE — Text-Stufen (ersetzen 900+ fontSize-Stellen)
  // ═══════════════════════════════════════════════════════════
  textXs: { fontSize: 10 },
  textSm: { fontSize: 11 },
  textBase: { fontSize: 12 },
  textMd: { fontSize: 13 },
  textLg: { fontSize: 14 },

  // Text + Gewicht Combos (häufigste Kombinationen)
  label: { fontSize: 11, fontWeight: 700 },
  labelMd: { fontSize: 12, fontWeight: 700 },
  labelLg: { fontSize: 13, fontWeight: 600 },
  caption: { fontSize: 12, color: 'var(--muted)' },
  captionSm: { fontSize: 11, color: 'var(--muted)' },
  captionXs: { fontSize: 10, color: 'var(--muted)' },

  // ═══════════════════════════════════════════════════════════
  // OBERFLÄCHEN — Container/Karten (ersetzen 230+ bg-Stellen)
  // ═══════════════════════════════════════════════════════════
  surface: {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '10px 14px',
  },
  surfaceRound: {
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 14,
    padding: 16,
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 20,
    background: 'var(--surface2)',
  },
  tag: {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
    borderRadius: 6,
    letterSpacing: 0.3,
  },

  // ═══════════════════════════════════════════════════════════
  // ICON-BUTTONS (32×32 quadratisch, in Tabellen/Listen)
  // ═══════════════════════════════════════════════════════════
  iconBtn: {
    background: 'transparent',
    border: '1px solid var(--border)',
    color: 'var(--muted)',
    cursor: 'pointer',
    padding: 6,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    transition: 'background-color .15s',
  },
  iconBtnDanger: {
    background: 'rgba(239,68,68,.06)',
    border: '1px solid rgba(239,68,68,.15)',
    color: 'var(--danger)',
    cursor: 'pointer',
    padding: 6,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },

  // ═══════════════════════════════════════════════════════════
  // TABELLEN
  // ═══════════════════════════════════════════════════════════
  thHead: {
    textAlign: 'left',
    padding: '8px 12px',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: 'var(--muted)',
    borderBottom: '1px solid var(--border)',
  },
  tdCell: { padding: '11px 12px' },

  // ═══════════════════════════════════════════════════════════
  // LISTEN — Zeilen und Trenner
  // ═══════════════════════════════════════════════════════════
  listRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderBottom: '1px solid var(--border)',
  },
  divider: { borderBottom: '1px solid var(--border)' },
  alertBox: {
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 12,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  accordionBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '14px 18px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: "'DM Sans',sans-serif",
    color: 'var(--text)',
  },
  dataRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
    fontSize: 12,
  },

  // ═══════════════════════════════════════════════════════════
  // BUTTONS (bestehend + neu)
  // ═══════════════════════════════════════════════════════════
  btnAmber: {
    background: 'linear-gradient(135deg,#d97706,#f59e0b)',
    color: '#fff',
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 700,
    border: 'none',
    borderRadius: 10,
    padding: '11px 20px',
    cursor: 'pointer',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 4px 16px rgba(245,158,11,.25), inset 0 1px 0 rgba(255,255,255,.15)',
    transition: 'transform .2s, box-shadow .2s',
    letterSpacing: '-.2px',
  },
  btnP: {
    background: '#16a34a',
    color: '#fff',
    fontFamily: "'DM Sans',sans-serif",
    fontWeight: 600,
    border: 'none',
    borderRadius: 10,
    padding: '11px 20px',
    cursor: 'pointer',
    fontSize: 13,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 1px 2px rgba(0,0,0,.05)',
    transition: 'background-color .15s, transform .15s',
    letterSpacing: '-.2px',
  },
  btnG: {
    background: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '10px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'background-color .15s, border-color .15s',
  },
  btnGSm: {
    background: 'var(--surface)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 11,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    transition: 'background-color .15s, border-color .15s',
  },
  btnDanger: {
    background: 'transparent',
    color: '#ef4444',
    border: '1px solid #ef4444',
    borderRadius: 10,
    padding: '10px 18px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all .2s',
  },

  // ═══════════════════════════════════════════════════════════
  // FORMULARE
  // ═══════════════════════════════════════════════════════════
  input: {
    width: '100%',
    boxSizing: 'border-box',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    padding: '11px 14px',
    color: 'var(--text)',
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 14,
    outline: 'none',
    transition: 'border-color .15s, box-shadow .15s',
  },

  // ═══════════════════════════════════════════════════════════
  // KARTEN & CONTAINER (bestehend)
  // ═══════════════════════════════════════════════════════════
  card: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 22,
    boxShadow: '0 1px 3px rgba(0,0,0,.04)',
    transition: 'transform .15s cubic-bezier(.2,0,.1,1), box-shadow .15s',
  },

  // ═══════════════════════════════════════════════════════════
  // TYPOGRAFIE — Überschriften (bestehend)
  // ═══════════════════════════════════════════════════════════
  heading: {
    fontFamily: "'DM Sans',sans-serif",
    fontSize: 24,
    fontWeight: 800,
    marginBottom: 24,
    letterSpacing: '-.5px',
  },
  subhead: { fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: 15, letterSpacing: '-.3px' },

  // ═══════════════════════════════════════════════════════════
  // GRID (bestehend)
  // ═══════════════════════════════════════════════════════════
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
  grid3: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 },
  grid4: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 },

  // ═══════════════════════════════════════════════════════════
  // STATUS & FEEDBACK (bestehend)
  // ═══════════════════════════════════════════════════════════
  error: {
    background: 'rgba(239,68,68,.08)',
    border: '1px solid rgba(239,68,68,.2)',
    borderRadius: 10,
    padding: '10px 14px',
    color: '#ef4444',
    fontSize: 13,
    marginTop: 10,
  },
  emptyState: { textAlign: 'center', padding: 60, color: 'var(--muted)', fontSize: 14 },
  filterBar: {
    display: 'flex',
    gap: 4,
    background: 'var(--surface2)',
    borderRadius: 11,
    padding: 3,
    backdropFilter: 'blur(8px)',
  },
  priceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '10px 14px',
    background: 'var(--surface2)',
    borderRadius: 10,
    border: '1px solid var(--border)',
  },
} as const satisfies Record<string, CSSProperties>;
